// Habits (docs/63; the Mac's boards 21 and 22, the shared boards 1–17): the badge at a habit day's row end, the week in
// its open card, How Often, Habits (Today | All), the habit's page (its open card grown into the content area), an
// area's habits, a break's sheet and a milestone's moment. The rules are the core's (core/habits.ts).
//
// A habit behaves as a to-do (Bastian, 2026-10-26): a click on its row selects it, a double-click or Return opens the
// to-do's own card in place, on today's day of it (`habitCardDay`; on a day it isn't due, one not stored), and Expand
// there opens its page. New Habit makes one without a name and opens that card, as New does for a to-do.
//
// The badge is strong (white on lilac) while today asks for the habit and it isn't done, soft (the tint) once done or
// on a day it isn't due; it shows the number alone, for days, weeks and months alike (round 3, 2026-10-25).

import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent, type MouseEvent, type ReactNode, type RefObject } from "react";
import { addDays, dayParts, weekday, weekStart } from "../core/days";
import {
  currentPause, habitBadge, habitsDoneLast, habitStatus, isPaused, periodDone, milestoneSpan, MILESTONES, nextPlannedDay, scheduleStart, habitSteps, habitStart, isFlexible, isScheduled, sameSchedule, sortedHabits, targetOf, unitOf,
  type HabitBadge, type HabitPeriodTick, type HabitProgress, type HabitStep, type HabitUnit,
} from "../core/habits";
import type { Route } from "../core/lists";
import { habitTodoID } from "../core/habits";
import type { Habit, HabitSchedule, Todo } from "../core/records";
import type { Day, Weekday } from "../core/types";
import { t } from "../i18n";
import { useWindow, type HabitWeekToast, type UI } from "./context";
import { createPortal } from "react-dom";
import { columnRowDropProps, draggedIDs, rowDragProps } from "./dnd";
import type { Model, ModelState } from "./model";
import { formatDay, shortDate, splitEmoji, timeLabel } from "./format";
import { Icon } from "./Icon";
import { ListHeader } from "./ListHeader";
import { ContextMenu, Menu, Popover, ViewSwitch, type MenuItem } from "./Menu";
import { ReminderPicker } from "./Pickers";
import { applyChange } from "./Properties";
import { prefs, setPrefs, usePrefs } from "./prefs";
import { Dialog } from "./Sheets";
import { completionSound } from "./sound";
import { tickedInCard } from "./tickedInCard";
import { TodoRow } from "./TodoRow";
import { useRowTouch } from "./touch";

// MARK: - Reading a habit

/** The habit a to-do is a day of, with its numbers today; null for any other to-do. */
export function useHabitOf(todo: Pick<Todo, "habitID">): { habit: Habit; progress: HabitProgress; badge: HabitBadge } | null {
  const { state, model } = useWindow();
  const prefs = usePrefs();
  const habit = todo.habitID === undefined ? undefined : state.records.habits.find((other) => other.id === todo.habitID);
  // Worked out again whenever the account changes (its to-dos are its history).
  return useMemo(() => {
    if (!habit) return null;
    const progress = model.habitProgress(habit);
    return { habit, progress, badge: habitBadge(progress, prefs.showsStreaks) };
  }, [habit, model, prefs.showsStreaks, state.records.todos, state.today]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** A habit's status in words, with the row's other details: "2 of 3 this week", "missed yesterday" (`habitStatus`). */
export function badgeDetail(detail: HabitBadge["detail"]): string | undefined {
  if (!detail) return undefined;
  if (detail.kind === "missed") return t("missed yesterday");
  return detail.kind === "week"
    ? t("{done} of {target} this week", { done: detail.done, target: detail.target })
    : t("{done} of {target} this month", { done: detail.done, target: detail.target });
}

function streakSpoken(progress: HabitProgress, isBest: boolean): string {
  const count = progress.streak;
  if (isBest) {
    return progress.unit === "day" ? t("Best streak, {count} days in a row", { count })
      : progress.unit === "week" ? t("Best streak, {count} weeks in a row", { count })
      : t("Best streak, {count} months in a row", { count });
  }
  return progress.unit === "day" ? t("Streak, {count} days in a row", { count })
    : progress.unit === "week" ? t("Streak, {count} weeks in a row", { count })
    : t("Streak, {count} months in a row", { count });
}

const reducesMotion = () => typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

/** A debug switch of the demo, for pictures (`?demo&…`); never in a build. */
function debugParam(name: string): string | null {
  if (!import.meta.env.DEV || typeof location === "undefined") return null;
  return new URLSearchParams(location.search).get(name);
}

/** `?demo&chipBloomStill`: a chip that blooms stays halfway through, the number rolling (for pictures). */
const chipBloomStill = () => debugParam("chipBloomStill") !== null;

/** The bloom of a tick (board 12): the chip swells with the old number, rolls up to the new one, then settles pale. */
interface Bloom { phase: "swell" | "roll" | "settle"; from?: number; to?: number; rose: boolean; strong: boolean; best: boolean }

/**
 * The badge at a row's end: strong while it waits for you, soft once done; the flame alone for a new habit. Every tick
 * of its day blooms (board 12, Bastian 2026-10-31): 0–0.2 s it grows by a third with a lilac glow, its flame filled;
 * 0.2–0.5 s the number rolls up to the new one, "+1" rises (when it rose) and sparks leave it; 0.5–0.8 s it springs
 * back and turns pale. Reduced motion: the number just changes. Taking a tick back doesn't bloom.
 */
export function HabitBadgeView({ badge, progress, large = false }: { badge: HabitBadge; progress: HabitProgress; large?: boolean }) {
  const done = progress.isDoneToday;
  const seen = useRef({ done, number: badge.number, isStrong: badge.isStrong, isBest: badge.isBest });
  const [bloom, setBloom] = useState<Bloom | null>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  useLayoutEffect(() => {
    const before = seen.current;
    seen.current = { done, number: badge.number, isStrong: badge.isStrong, isBest: badge.isBest };
    if (!done || before.done || reducesMotion()) return;
    for (const timer of timers.current) clearTimeout(timer);
    const rose = badge.number !== undefined && (before.number ?? 0) < badge.number;
    const start: Bloom = { phase: "swell", rose, strong: before.isStrong, best: before.isBest };
    if (before.number !== undefined) start.from = before.number;
    if (badge.number !== undefined) start.to = badge.number;
    if (chipBloomStill()) return setBloom({ ...start, phase: "roll" });
    setBloom(start);
    timers.current = [
      setTimeout(() => setBloom((bloom) => bloom && { ...bloom, phase: "roll" }), 200),
      setTimeout(() => setBloom((bloom) => bloom && { ...bloom, phase: "settle" }), 500),
      setTimeout(() => setBloom(null), 850),
    ];
  }, [done, badge.number, badge.isStrong, badge.isBest]);
  useEffect(() => () => { for (const timer of timers.current) clearTimeout(timer); }, []);
  // While it swells and rolls it keeps the look it had before the tick; it settles into the one it has now.
  const looksBefore = bloom !== null && bloom.phase !== "settle";
  const strong = looksBefore ? bloom.strong : badge.isStrong;
  const best = looksBefore ? bloom.best : badge.isBest;
  const shown = bloom?.phase === "swell" ? bloom.from : badge.number;
  const number = bloom?.phase === "roll" && bloom.rose
    ? <span className="badge-roll"><span className="old">{bloom.from}</span><span className="new">{bloom.to}</span></span>
    : shown;
  return (
    <span className={`habit-badge${strong ? " strong" : " soft"}${best ? " best" : ""}${large ? " large" : ""}${shown === undefined && !(bloom?.phase === "roll" && bloom.rose) ? " flame-only" : ""}${bloom ? ` blooming ${bloom.phase}` : ""}${bloom && chipBloomStill() ? " still" : ""}`}
      role="img" aria-label={streakSpoken(progress, badge.isBest)}>
      <Icon name="flame" />{number}
      {bloom?.phase === "roll" && (
        <span className="badge-sparks" aria-hidden="true">
          {bloom.rose && <b className="badge-plus">+1</b>}
          {SPARKS.map((spark, index) => <i key={index} style={{ "--dx": `${spark[0]}px`, "--dy": `${spark[1]}px`, background: spark[2] } as CSSProperties} />)}
        </span>
      )}
    </span>
  );
}

/** Where the sparks fly from the chip, and their colours: lilac, mint, a touch of gold (board 12). */
const SPARKS: readonly (readonly [number, number, string])[] = [
  [-26, -14, "var(--habit-fill)"], [-30, 12, "var(--mint)"], [4, -20, "#F2C94C"], [22, -12, "var(--habit-strong)"], [20, 14, "var(--mint)"], [-8, 18, "var(--habit-fill)"],
];

/** How Often in words: "Every day", "Mon, Wed, Fri", "3 times a week" (HabitSchedule.title). */
export function scheduleLabel(schedule: HabitSchedule): string {
  switch (schedule.kind) {
    case "everyNDays": {
      const every = Math.max(schedule.interval ?? 1, 1);
      return every === 1 ? t("Every day") : t("Every {count} days", { count: every });
    }
    case "weekdays": {
      const days = schedule.weekdays ?? [];
      if (days.length === 7) return t("Every day");
      if ([2, 3, 4, 5, 6].every((day) => days.includes(day)) && days.length === 5) return t("Every weekday");
      return WEEK.filter((day) => days.includes(day)).map((day) => weekdayName(day, "short")).join(", ");
    }
    case "timesPerWeek": return targetOf(schedule) === 1 ? t("Once a week") : t("{count} times a week", { count: targetOf(schedule) });
    case "timesPerMonth": return targetOf(schedule) === 1 ? t("Once a month") : t("{count} times a month", { count: targetOf(schedule) });
    case "monthDays": return t("Monthly on {days}", { days: [...(schedule.monthDays ?? [])].sort((a, b) => a - b).map((day) => `${day}.`).join(", ") });
    default: return t("Set in a newer version");
  }
}

/** Monday first, as the week begins in the web (firstWeekday 2). */
const WEEK: readonly Weekday[] = [2, 3, 4, 5, 6, 7, 1];

/** A weekday's name: "M", "Mon", from a week known to begin on Monday 5 October 2026. */
function weekdayName(day: number, width: "narrow" | "short"): string {
  const monday = 5;
  const date = monday + ((day - 2 + 7) % 7);
  return formatDay(`2026-10-${String(date).padStart(2, "0")}` as Day, { weekday: width });
}

// MARK: - The week in the open card

/** This week, a dot a day: done, kept (dashed), missed, today's ring, days to come; a weekly habit, a dot a week. */
export function WeekStrip({ habit, today }: { habit: Habit; today: Day }) {
  const { model, state } = useWindow();
  const done = model.habitDoneDays(habit.id);
  const steps = habitSteps(habit, done as Set<Day>, today, state.zone);
  if (isFlexible(habit.schedule)) {
    const current = steps.at(-1);
    const target = current?.target ?? targetOf(habit.schedule);
    const count = current?.done ?? 0;
    const weeks = steps.slice(-9);
    return (
      <div className="habit-week flexible">
        <span className="habit-label">{unitOf(habit.schedule) === "week"
          ? t("This Week · {done} of {target}", { done: count, target }) : t("This Month · {done} of {target}", { done: count, target })}</span>
        <div className="habit-slots">
          {Array.from({ length: target }, (_, index) => (
            <span key={index} className={`habit-slot${index < count ? " done" : index === count ? " next" : ""}`}>{index < count && <Icon name="check" />}</span>
          ))}
        </div>
        <span className="habit-label">{unitOf(habit.schedule) === "week" ? t("Weeks") : t("Months")}</span>
        <div className="habit-dots" aria-label={unitOf(habit.schedule) === "week" ? t("Weeks") : t("Months")}>
          {weeks.map((step) => <span key={step.first} className={`habit-dot ${step.state}`} title={shortDate(step.first, today)} />)}
        </div>
      </div>
    );
  }
  const monday = weekStart(today, 2);
  const start = habitStart(habit, state.zone);
  const byDay = new Map(steps.map((step) => [step.first, step]));
  return (
    <div className="habit-week">
      {WEEK.map((wd, index) => {
        const day = addDays(monday, index);
        const step = byDay.get(day);
        const scheduled = isScheduled(habit.schedule, day, start) && day >= start;
        const state = step ? step.state : day > today && scheduled ? "coming" : "none";
        return (
          <div key={day} className={`habit-day${day === today ? " today" : ""}`}>
            <span className="habit-day-name">{weekdayName(wd, "narrow")}</span>
            <span className={`habit-dot ${state}${day === today ? " ring" : ""}`} title={formatDay(day, { weekday: "long", day: "numeric", month: "short" })}>
              {state === "done" && <Icon name="check" />}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// MARK: - How Often

/** Daily, Weekly or Monthly; on set days or N times; and Never Miss Twice (board 14). */
export function HowOftenPicker({ habit, onChange }: { habit: Habit; onChange: (habit: Habit) => void }) {
  const schedule = habit.schedule;
  const period: "daily" | "weekly" | "monthly" = schedule.kind === "everyNDays" ? "daily"
    : schedule.kind === "weekdays" || schedule.kind === "timesPerWeek" ? "weekly" : "monthly";
  const anyDays = isFlexible(schedule);
  const set = (next: HabitSchedule) => onChange({ ...habit, schedule: next });
  const choosePeriod = (next: typeof period) => {
    if (next === "daily") set({ kind: "everyNDays", interval: 1 });
    if (next === "weekly") set({ kind: "weekdays", weekdays: [2, 3, 4, 5, 6] });
    if (next === "monthly") set({ kind: "monthDays", monthDays: [1] });
  };
  const toggleDay = (list: readonly number[], day: number) => (list.includes(day) ? list.filter((other) => other !== day) : [...list, day]);
  return (
    <div className="how-often" role="group" aria-label={t("How Often")}>
      <div className="segmented" role="radiogroup" aria-label={t("How Often")}>
        {(["daily", "weekly", "monthly"] as const).map((choice) => (
          <button key={choice} role="radio" aria-checked={period === choice} className={period === choice ? "on" : ""} onClick={() => choosePeriod(choice)}>
            {choice === "daily" ? t("Daily") : choice === "weekly" ? t("Weekly") : t("Monthly")}
          </button>
        ))}
      </div>
      {period === "daily" ? (
        <label className="how-often-row">
          <span>{t("Every")}</span>
          <select className="field" value={Math.max(schedule.interval ?? 1, 1)} aria-label={t("Every how many days")}
            onChange={(event) => set({ kind: "everyNDays", interval: Number(event.target.value) })}>
            {[1, 2, 3, 4, 5, 6, 7].map((count) => <option key={count} value={count}>{count === 1 ? t("Day") : t("{count} Days", { count })}</option>)}
          </select>
        </label>
      ) : (
        <>
          <div className="segmented small" role="radiogroup" aria-label={t("Which days")}>
            <button role="radio" aria-checked={!anyDays} className={!anyDays ? "on" : ""}
              onClick={() => set(period === "weekly" ? { kind: "weekdays", weekdays: [2, 3, 4, 5, 6] } : { kind: "monthDays", monthDays: [1] })}>
              {t("On These Days")}
            </button>
            <button role="radio" aria-checked={anyDays} className={anyDays ? "on" : ""}
              onClick={() => set(period === "weekly" ? { kind: "timesPerWeek", count: 3 } : { kind: "timesPerMonth", count: 4 })}>
              {t("On Any Days")}
            </button>
          </div>
          {anyDays ? (
            <div className="how-often-row stepper">
              <button className="small-button" aria-label={t("Fewer")} disabled={targetOf(schedule) <= 1}
                onClick={() => set({ ...schedule, count: targetOf(schedule) - 1 })}>−</button>
              <b>{targetOf(schedule)}</b>
              <button className="small-button" aria-label={t("More")} disabled={targetOf(schedule) >= (period === "weekly" ? 7 : 31)}
                onClick={() => set({ ...schedule, count: targetOf(schedule) + 1 })}>+</button>
              <span>{period === "weekly" ? t("times a week") : t("times a month")}</span>
            </div>
          ) : period === "weekly" ? (
            <div className="weekday-toggles">
              {WEEK.map((day) => {
                const on = (schedule.weekdays ?? []).includes(day);
                return (
                  <button key={day} className={on ? "on" : ""} aria-pressed={on} aria-label={weekdayName(day, "short")}
                    onClick={() => { const next = toggleDay(schedule.weekdays ?? [], day); if (next.length) set({ kind: "weekdays", weekdays: next }); }}>
                    {weekdayName(day, "narrow")}
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="month-toggles">
              {Array.from({ length: 31 }, (_, index) => index + 1).map((day) => {
                const on = (schedule.monthDays ?? []).includes(day);
                return (
                  <button key={day} className={on ? "on" : ""} aria-pressed={on}
                    onClick={() => { const next = toggleDay(schedule.monthDays ?? [], day); if (next.length) set({ kind: "monthDays", monthDays: next }); }}>
                    {day}
                  </button>
                );
              })}
            </div>
          )}
        </>
      )}
      <label className="toggle-row">
        <span>{t("Never Miss Twice")}</span>
        <input type="checkbox" role="switch" checked={habit.neverMissTwice ?? true}
          onChange={(event) => {
            const next = { ...habit };
            if (event.target.checked) delete next.neverMissTwice; else next.neverMissTwice = false;
            onChange(next);
          }} />
      </label>
      <p className="settings-note">{isFlexible(schedule)
        ? t("A week that falls short once keeps the streak; two in a row end it.")
        : t("Missing once keeps the streak; two in a row end it.")}</p>
    </div>
  );
}

/**
 * How Often in a popover. Never Miss Twice is the habit's at once; a new rule waits until the popover closes and is
 * asked about first, so it never changes by accident (Bastian, 2026-10-27: board E2).
 */
function HowOftenPopover({ habit, anchor, onClose }: { habit: Habit; anchor: RefObject<HTMLElement | null>; onClose: () => void }) {
  const window = useWindow();
  const [chosen, setChosen] = useState<HabitSchedule | undefined>(undefined);
  const shown = chosen ? { ...habit, schedule: chosen } : habit;
  const close = () => {
    onClose();
    if (!chosen || sameSchedule(chosen, habit.schedule)) return;
    // A habit begun today has no past to change: it just takes the rule.
    if (habitStart(habit, window.state.zone) < window.state.today) askHowOften(window, habit, chosen);
    else window.model.updateHabit({ ...habit, schedule: chosen }, "How Often");
  };
  return (
    <Popover anchor={anchor} onClose={close} label={t("How Often")} className="how-often-popover">
      <HowOftenPicker habit={shown} onChange={(next) => {
        if (!sameSchedule(next.schedule, shown.schedule)) setChosen(next.schedule);
        else window.model.updateHabit({ ...next, schedule: habit.schedule }, "How Often");
      }} />
    </Popover>
  );
}

/** The question before a new rule counts: what it does to the streak (HabitSchedule.changeNote), then Keep or Change. */
function askHowOften(window: ReturnType<typeof useWindow>, habit: Habit, next: HabitSchedule) {
  const before = unitOf(habit.schedule);
  const after = unitOf(next);
  const note = before === after
    ? after === "day" ? t("The streak goes on; the new rule counts from today.")
      : after === "week" ? t("The streak goes on; the new rule counts from this week.")
        : t("The streak goes on; the new rule counts from this month.")
    : after === "day" ? t("What you did stays in the history; the streak starts again, counted in days.")
      : after === "week" ? t("What you did stays in the history; the streak starts again, counted in weeks, from this week.")
        : t("What you did stays in the history; the streak starts again, counted in months, from this month.");
  window.showSheet({
    kind: "confirm", quiet: true, title: t("Change How Often?"),
    text: `${habit.title}: ${scheduleLabel(habit.schedule)} → ${scheduleLabel(next)}. ${note}`,
    action: t("Change"), cancel: t("Don’t Change"),
    run: () => { window.model.updateHabit({ ...habit, schedule: next }, "How Often"); },
  });
}

// MARK: - In the open card

/** What a habit day's card shows instead of a to-do's chips (boards 5 and 6): the week, the streak, How Often, the reminder. */
export function HabitCardBody({ todo, habit, progress, badge, onReminder, onMakeTodo }: {
  todo: Todo; habit: Habit; progress: HabitProgress; badge: HabitBadge; onReminder: (todo: Todo) => void;
  /** The flame again (the card's `makeTodoAgain`). */
  onMakeTodo: () => void;
}) {
  const { model, state, showToast, showSheet, openTodo } = useWindow();
  const [picker, setPicker] = useState<"often" | "reminder" | null>(null);
  const often = useRef<HTMLButtonElement>(null);
  const reminder = useRef<HTMLButtonElement>(null);
  // The streak's number at the end of the week once there is one (Bastian, 2026-10-27: no flame, no words).
  const streak = badge.number;
  return (
    <div className="habit-card">
      <div className="habit-week-row">
        <WeekStrip habit={habit} today={state.today} />
        {streak !== undefined && (
          <span className={`habit-card-streak${badge.isBest ? " best" : ""}`} aria-label={streakSpoken(progress, badge.isBest)}>{streak}</span>
        )}
      </div>
      {habitStatus(badge, todo.completedAt !== undefined)?.kind === "missed" && <div className="habit-streak-line habit-text">{badgeDetail(badge.detail)}</div>}
      <div className="chips">
        <button className="chip habit-chip" data-slot="flame" title={t("Make a To-Do")} aria-label={t("Habit: make it a to-do again")}
          onClick={() => askMakeTodo(showSheet, habit, onMakeTodo)}>
          <Icon name="flameFill" />{t("Habit")}
        </button>
        <button ref={often} className="chip" data-slot="often" title={t("How Often")} onClick={() => setPicker("often")}>
          <Icon name="repeat" />{scheduleLabel(habit.schedule)}
        </button>
        <button ref={reminder} data-slot="reminder" className={todo.reminder ? "chip" : "tool"} title={t("Reminder")}
          aria-label={todo.reminder ? `${t("Reminder")}: ${timeLabel(todo.reminder, state.zone)}` : t("Reminder")} onClick={() => setPicker("reminder")}>
          <Icon name={todo.reminder ? "bellFill" : "bell"} />{todo.reminder && timeLabel(todo.reminder, state.zone)}
        </button>
      </div>
      {picker === "often" && <HowOftenPopover habit={habit} anchor={often} onClose={() => setPicker(null)} />}
      {picker === "reminder" && (
        <Popover anchor={reminder} onClose={() => setPicker(null)} label={t("Reminder")}>
          <ReminderPicker todo={todo} today={state.today} zone={state.zone} onKeep={() => setPicker(null)}
            onChoose={(time) => { onReminder(applyChange(todo, { reminder: time ?? null }, state.today, state.zone)); setPicker(null); }} />
        </Popover>
      )}
      {todo.completedAt === undefined && model.canRestHabitToday(habit.id) && (
        <div className="habit-not-today">
          <button className="link" onClick={() => { openTodo(null); restToday(model, habit.id, showToast); }}>{t("Not Today")}</button>
        </div>
      )}
      {model.restsHabitToday(habit.id) && (
        // Off today: the way back, where Not Today was (Bastian, 2026-10-30).
        <div className="habit-not-today">
          <button className="link" onClick={() => unrestToday(model, habit.id, showToast)}>{t("Back to Today")}</button>
        </div>
      )}
    </div>
  );
}

/** Not Today (docs/63 15d): the habit's day leaves Today, with Undo in the note. Offered only where canRestHabitToday. */
export function restToday(model: Model, habitID: string, showToast: (text: string, undo?: boolean) => void) {
  model.restHabitToday(habitID);
  showToast(t("Not Today"), true);
}

/** Back to Today (docs/63 15d, 2026-10-30): Not Today taken back, the habit's day is in Today again, with Undo. */
export function unrestToday(model: Model, habitID: string, showToast: (text: string, undo?: boolean) => void) {
  model.unrestHabitToday(habitID);
  showToast(t("Back to Today"), true);
}

/** A habit off today, as its status line says it: the moon and "Not today" (the apps' `HabitRow.notToday`). */
function NotTodayLabel() {
  return <span className="habit-rests"><Icon name="moon" />{t("Not today")}</span>;
}

/** A habit's name in a question: its title, or what a habit without one is called. */
function habitName(habit: Habit): string {
  return habit.title.trim() || t("New Habit");
}

/**
 * Make a To-Do asks first (Bastian, 2026-10-29): the habit is archived (its history stays in the account, no app shows
 * it), today's day stays as a plain to-do; one step of Undo right after. `run` does it: the menu's, or the open card's
 * `makeTodoAgain` (the flame chip and its ⋯ menu).
 */
export function askMakeTodo(showSheet: ReturnType<typeof useWindow>["showSheet"], habit: Habit, run: () => void) {
  showSheet({
    // Not a deletion: the main action's look (the Mac and the iPhone: a plain button, Delete's is the red one).
    kind: "confirm", quiet: true, title: t("Make “{name}” a To-Do?", { name: habitName(habit) }), action: t("Make a To-Do"),
    text: t("It stays as a to-do and leaves Habits. You can take this back with Undo right after; made a habit again later, its history comes back, and the days you ticked meanwhile count too."),
    run,
  });
}

/** The ⋯ menu's habit items: Not Today, a break, back to a to-do (`makeTodo`, the open card's own), delete. */
export function useHabitMenu(habit: Habit | undefined, close: () => void, makeTodo?: () => void): MenuItem[] {
  const { model, state, showSheet, showToast } = useWindow();
  if (!habit) return [];
  const items: MenuItem[] = [];
  const pause = currentPause(habit, state.today);
  if (model.canRestHabitToday(habit.id)) items.push({ label: t("Not Today"), icon: "moon", run: () => { close(); restToday(model, habit.id, showToast); } });
  if (model.restsHabitToday(habit.id)) items.push({ label: t("Back to Today"), icon: "today", run: () => unrestToday(model, habit.id, showToast) });
  items.push(pause
    ? { label: t("Resume"), icon: "flame", run: () => { model.resumeHabit(habit.id); showToast(t("Habit resumed"), true); } }
    : { label: t("Pause…"), icon: "pause", run: () => showSheet({ kind: "pauseHabit", habitID: habit.id }) });
  items.push({
    label: t("Make a To-Do"), icon: "flame",
    run: () => askMakeTodo(showSheet, habit, makeTodo ?? (() => { close(); model.turnIntoTodo(habit.id); showToast(t("Made a to-do"), true); })),
  });
  // The habit and all its days (its streak and history) go on all devices; one step of Undo right after.
  items.push({
    label: t("Delete Habit"), icon: "trash", destructive: true,
    run: () => showSheet({
      kind: "confirm", title: t("Delete “{name}”?", { name: habitName(habit) }), action: t("Delete Habit"),
      text: t("Its streak and every day you ticked are deleted on all your devices. You can take this back with Undo right after; later, it can’t be restored."),
      run: () => { close(); model.deleteHabit(habit.id); showToast(t("Habit deleted"), true); },
    }),
  });
  return items;
}

/**
 * A habit row's right-click menu (docs/63 15d; the Mac's row menu, MacTodoMenu.swift): Open, Complete, then the habit's
 * own items, Not Today among them while it may be taken (canRestHabitToday). With a mouse; touch has the swipe.
 */
export function useHabitRowMenu(habit: Habit | undefined, day: Todo, onComplete?: (ids: readonly string[]) => void) {
  const { layout, ui, select, openTodo, model } = useWindow();
  const [at, setAt] = useState<{ x: number; y: number } | null>(null);
  const close = useCallback(() => setAt(null), []);
  const habitItems = useHabitMenu(habit, () => {});
  const onContextMenu = (event: MouseEvent) => {
    if (!habit || layout.touch || ui.open !== null || ui.composing !== null) return;
    event.preventDefault();
    if (!ui.selected.includes(day.id)) select(day.id);
    setAt({ x: event.clientX, y: event.clientY });
  };
  let menu: ReactNode = null;
  if (at && habit) {
    const items: MenuItem[] = [{ label: t("Open"), icon: "expand", run: () => openTodo(day.id) }];
    if (onComplete && !model.isUnstoredHabitDay(day)) {
      items.push({ label: day.completedAt ? t("Mark as Not Done") : t("Complete"), icon: "check", run: () => onComplete([day.id]) });
    }
    items.push(...habitItems);
    menu = <ContextMenu at={at} items={items} label={habit.title || t("Habit")} onClose={close} />;
  }
  return { onContextMenu, menu };
}

// MARK: - The tick

/**
 * After a tick: the habits' sound once; a flexible habit's day tells its week in the toast (boards 9 and 10); and a
 * milestone's moment when one was reached, over the toast.
 */
export function habitTicked(window: ReturnType<typeof useWindow>, todos: readonly Todo[]) {
  const only = todos.length === 1 ? todos[0] : undefined;
  const tick = only?.habitID === undefined ? undefined : window.model.habitPeriodTick(only.id);
  const habit = tick && only ? window.state.records.habits.find((other) => other.id === only.habitID) : undefined;
  if (tick && habit?.title.trim()) {
    const note: HabitWeekToast = { title: habit.title, tick };
    if (tick.closes && prefs().showsStreaks && tick.streak >= 2) note.streak = tick.streak;
    window.showToast(t("Completed"), true, note);
  }
  showMilestone(window, todos);
}

/**
 * A tick (docs/70; Bastian, 2026-11-03: "the question must come first, before the to-do/habit moves out"): one habit's
 * day ticked the morning after a missed planned day asks "yesterday or today?" before anything is ticked. Yesterday
 * counts the day before and today's box stays open; Today (its button, Esc or a click outside) carries out `tick`.
 * Anything else ticks at once. `todos` are those the tick would complete.
 */
export function tickAskingFirst(window: ReturnType<typeof useWindow>, todos: readonly Todo[], tick: () => void) {
  const only = todos.length === 1 ? todos[0] : undefined;
  const late = only && only.completedAt === undefined ? window.model.lateHabitTick(only) : undefined;
  if (!late) return tick();
  window.showSheet({
    kind: "confirm", quiet: true, title: t("Missed Yesterday"),
    text: t("You didn’t tick “{title}” yesterday. Was this for yesterday or for today?", { title: late.title }),
    action: t("Yesterday"), cancel: t("Today"),
    run: () => { tickSound([only!]); window.model.countForYesterday(late); },
    onCancel: tick,
  });
}

/** A milestone's moment when one of the ticked habit days reached one, over the toast. */
function showMilestone(window: ReturnType<typeof useWindow>, todos: readonly Todo[]) {
  for (const todo of todos) {
    if (todo.habitID === undefined) continue;
    const milestone = window.model.newHabitMilestone(todo.habitID);
    if (milestone !== undefined) {
      window.showSheet({ kind: "milestone", habitID: todo.habitID, count: milestone });
      return;
    }
  }
}

// MARK: - The week in the toast (boards 9 and 10, Bastian 2026-10-31)

type WeekPhase = "completed" | "dots" | "closing" | "done" | "progress";
const WEEK_PHASES: readonly WeekPhase[] = ["completed", "dots", "closing", "done", "progress"];

/** `?demo&weekToastStill=dots|closing|done|progress`: the toast stays, frozen in that state (for pictures). */
export function weekToastStill(): WeekPhase | null {
  const value = debugParam("weekToastStill");
  return value !== null && (WEEK_PHASES as readonly string[]).includes(value) ? value as WeekPhase : null;
}

/** More times than dots fit (a month's): a ring that fills instead. */
const MAX_DOTS = 8;

/**
 * The toast after a tick of a flexible habit's day. Before the week is done: a dot for each time planned (filled for
 * done), "Sport" and "2 of 4 this week". The tick that closes it: "Completed" first; at 0.3 s two lines, the last dot
 * filling with a pulse; 0.6–1.1 s the dots gather into a ring that closes, a few sparks leaving it; then a tick in the
 * ring, "Sport: week done" and "6 weeks in a row". The toast keeps its look; the lilac is in the dots, the ring and
 * the line under the name. Reduced motion: the final state at once.
 */
export function HabitWeekToastView({ note }: { note: HabitWeekToast }) {
  const { tick } = note;
  const still = weekToastStill();
  const [phase, setPhase] = useState<WeekPhase>(() => {
    if (!tick.closes) return "progress";
    if (still && still !== "progress") return still;
    return reducesMotion() ? "done" : "completed";
  });
  useEffect(() => {
    if (!tick.closes || still || reducesMotion()) return;
    const timers = [setTimeout(() => setPhase("dots"), 300), setTimeout(() => setPhase("closing"), 600), setTimeout(() => setPhase("done"), 1100)];
    return () => { for (const timer of timers) clearTimeout(timer); };
  }, [tick.closes, still]);
  const month = tick.unit === "month";
  const count = month ? t("{done} of {target} this month", { done: tick.done, target: tick.target })
    : t("{done} of {target} this week", { done: tick.done, target: tick.target });
  const doneTitle = month ? t("{name}: month done", { name: note.title }) : t("{name}: week done", { name: note.title });
  const doneDetail = note.streak === undefined ? count
    : month ? t("{count} months in a row", { count: note.streak }) : t("{count} weeks in a row", { count: note.streak });
  const spoken = tick.closes ? `${doneTitle}, ${doneDetail}` : `${note.title}, ${count}`;
  if (phase === "completed") {
    return <span className="habit-toast-body"><span aria-hidden="true">{t("Completed")}</span><span className="visually-hidden">{spoken}</span></span>;
  }
  return (
    <span className={`habit-toast-body ${phase}${still ? " still" : ""}`}>
      <WeekMark tick={tick} phase={phase} />
      <span className="habit-toast-text" aria-hidden="true">
        <b>{phase === "done" ? doneTitle : note.title}</b>
        <span>{phase === "done" ? doneDetail : count}</span>
      </span>
      <span className="visually-hidden">{spoken}</span>
    </span>
  );
}

/** The dots of the week, or, past eight, a ring that fills; gathering into the ring that closes, and its tick. */
function WeekMark({ tick, phase }: { tick: HabitPeriodTick; phase: WeekPhase }) {
  const dots = Math.max(tick.target, 1);
  const filled = Math.min(tick.done, dots);
  const ring = phase === "closing" || phase === "done";
  if (dots > MAX_DOTS) {
    // A month of many times: the ring fills to the count; the closing tick draws its last part, then the tick.
    const share = phase === "dots" ? (filled - 1) / dots : filled / dots;
    return (
      <span className={`week-mark arc ${phase}`} aria-hidden="true">
        <svg viewBox="0 0 30 30" width="30" height="30">
          <circle className="week-arc-track" cx="15" cy="15" r="11.5" />
          <circle className="week-arc" cx="15" cy="15" r="11.5" pathLength={1} style={{ strokeDasharray: `${share} 1` }} />
          {phase === "done" && <path className="week-tick" pathLength={1} d="M10 15.5l3.4 3.4 6.6-7" />}
        </svg>
        {phase === "closing" && <WeekSparks />}
      </span>
    );
  }
  const width = dots * 9 + (dots - 1) * 5;
  return (
    <span className={`week-mark ${phase}`} aria-hidden="true" style={{ "--row-width": `${width}px` } as CSSProperties}>
      {Array.from({ length: dots }, (_, index) => {
        const angle = (-90 + (index * 360) / dots) * (Math.PI / 180);
        const style = {
          "--x0": `${index * 14}px`, "--y0": "10.5px",
          "--x1": `${(15 + 11 * Math.cos(angle) - 4.5).toFixed(2)}px`, "--y1": `${(15 + 11 * Math.sin(angle) - 4.5).toFixed(2)}px`,
        } as CSSProperties;
        const last = index === filled - 1;
        return <i key={index} className={`week-dot${index < filled ? " on" : ""}${last && tick.closes ? " last" : ""}`} style={style} />;
      })}
      {ring && (
        <svg viewBox="0 0 30 30" width="30" height="30">
          <circle className="week-ring" cx="15" cy="15" r="11.5" pathLength={1} />
          {phase === "done" && <path className="week-tick" pathLength={1} d="M10 15.5l3.4 3.4 6.6-7" />}
        </svg>
      )}
      {phase === "closing" && <WeekSparks />}
    </span>
  );
}

/** A few sparks leaving the ring as it closes, in lilac and mint. */
function WeekSparks() {
  const sparks: readonly (readonly [number, number, string])[] = [
    [2, -22, "var(--toast-habit)"], [-20, 2, "var(--mint)"], [22, -14, "var(--mint)"], [24, 14, "var(--toast-habit)"], [-6, 22, "var(--toast-habit-soft)"],
  ];
  return (
    <span className="week-sparks">
      {sparks.map((spark, index) => <i key={index} style={{ "--dx": `${spark[0]}px`, "--dy": `${spark[1]}px`, background: spark[2] } as CSSProperties} />)}
    </span>
  );
}

/** The sound for a tick of these to-dos: the habits' when one of them is a habit's day. */
export function tickSound(completing: readonly Todo[]) {
  completionSound(completing.length, completing.some((todo) => todo.habitID !== undefined));
}

/**
 * The box of a habit on a day it isn't due (a row, or the card of a day not stored): asked first, then the day is
 * counted done anyway (Store.countHabitToday), with the tick's sound and celebration. `counted` follows (the card closes).
 */
export function askCountAnyway(window: ReturnType<typeof useWindow>, day: Todo, counted?: () => void) {
  const count = () => {
    tickSound([day]);
    window.model.countHabitToday(day);
    window.showToast(t("Completed"), true);
    habitTicked(window, [day]);
    counted?.();
  };
  // The morning after a missed planned day, "yesterday or today?" takes the place of "Not Planned Today" (docs/70).
  const late = window.model.lateHabitTick(day);
  if (late) {
    return window.showSheet({
      kind: "confirm", quiet: true, title: t("Missed Yesterday"),
      text: t("You didn’t tick “{title}” yesterday. Was this for yesterday or for today?", { title: late.title }),
      action: t("Yesterday"), cancel: t("Today"),
      run: () => { tickSound([day]); window.model.countForYesterday(late); },
      onCancel: count,
    });
  }
  window.showSheet({
    kind: "confirm", quiet: true, title: t("Not Planned Today"), text: t("Count “{title}” as done anyway?", { title: day.title }), action: t("Count It"),
    run: count,
  });
}

// MARK: - The habit's page

/** The open card grown into the content area (boards 7, 8, 22): stats, the history, the month to change days. */
export function HabitPage({ id }: { id: string }) {
  const window = useWindow();
  const { state, model, set, showToast } = window;
  const habit = state.records.habits.find((other) => other.id === id);
  const today = state.today;
  const [month, setMonth] = useState<Day>(`${today.slice(0, 8)}01` as Day);
  const [title, setTitle] = useState(habit?.title ?? "");
  const [notes, setNotes] = useState(habit?.notes ?? "");
  const page = useRef<HTMLDivElement>(null);
  const collapse = () => {
    // Back into the card Expand grew: today's day, or in Habits and an area, the day not stored as well.
    const day = model.habitCardDay(id);
    const shown = day && (!model.isUnstoredHabitDay(day) || window.ui.route.kind === "habits" || window.ui.route.kind === "area");
    set({ habitPage: null, open: shown ? day.id : null, selected: shown ? [day.id] : [] });
  };
  const items = useHabitMenu(habit, () => set({ habitPage: null }));
  useEffect(() => {
    page.current?.focus({ preventScroll: true });
    const key = (event: globalThis.KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches("input, textarea, select") || target?.closest(".popover, .sheet-backdrop")) return;
      if (event.key === "Escape" || (event.key === "ArrowLeft" && !event.metaKey && !event.altKey)) { event.preventDefault(); collapse(); }
      if (event.key === " " && !target?.matches("button")) { event.preventDefault(); tick(); }
    };
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  });
  if (!habit) return null;
  const progress = model.habitProgress(habit);
  const done = model.habitDoneDays(habit.id) as Set<Day>;
  const steps = habitSteps(habit, done, today, state.zone);
  const todayTodo = state.records.todos.find((todo) => todo.habitID === id && todo.start.kind === "on" && todo.start.day === today);
  const unit = progress.unit;
  const area = state.lists.area(habit.areaID);
  const save = () => {
    const next = { ...habit, title: title.trim() || habit.title, notes };
    if (next.title !== habit.title || next.notes !== habit.notes) model.updateHabit(next);
  };
  const monthSteps = steps.filter((step) => step.first.slice(0, 7) === today.slice(0, 7));
  const planned = monthSteps.filter((step) => step.state !== "open").length + (monthSteps.at(-1)?.state === "open" && progress.isDoneToday ? 1 : 0);
  const doneInMonth = monthSteps.filter((step) => step.state === "done").length;
  const tick = () => {
    if (!todayTodo) return;
    const completing = todayTodo.completedAt === undefined;
    tickAskingFirst(window, completing ? [todayTodo] : [], () => {
      if (completing) tickSound([todayTodo]);
      model.toggleCompletion([todayTodo.id]);
      if (completing) { showToast(t("Completed"), true); habitTicked(window, [todayTodo]); }
    });
  };
  return (
    <div ref={page} className="habit-page" tabIndex={-1} aria-label={habit.title}>
      <div className="habit-page-head">
        {todayTodo ? (
          <button className={`check tickbox habit-box${todayTodo.completedAt ? " checked" : ""}`} aria-pressed={todayTodo.completedAt !== undefined}
            aria-label={todayTodo.completedAt ? t("Mark as Not Done") : t("Complete")} onClick={tick}>
            <Icon name="check" />
          </button>
        ) : !currentPause(habit, today) && model.habitCardDay(id) ? (
          // Not planned today: dashed, and a click asks before counting the day anyway.
          <button className="check tickbox habit-box not-planned" aria-pressed={false} aria-label={t("Complete")} title={t("Not planned today")}
            onClick={() => { const day = model.habitCardDay(id); if (day) askCountAnyway(window, day); }}>
            <Icon name="check" />
          </button>
        ) : <span className="habit-box-space" />}
        <input className="habit-page-title" value={title} aria-label={t("Title")} onChange={(event) => setTitle(event.target.value)} onBlur={save}
          onKeyDown={(event) => { if (event.key === "Enter") (event.target as HTMLInputElement).blur(); }} />
        <button className="glass collapse-button" title={`${t("Collapse")} (Esc)`} aria-label={t("Collapse")} onClick={collapse}><Icon name="collapse" /></button>
        <Menu label={t("More")} items={items} icon="more" className="glass" />
      </div>
      <div className="habit-page-chips">
        <HowOftenChip habit={habit} />
        {habit.reminder && <span className="chip static"><Icon name="bellFill" />{timeLabel(habit.reminder, habit.reminderZone ?? state.zone)}</span>}
        <select className="field area-select" value={habit.areaID ?? ""} aria-label={t("Area")}
          onChange={(event) => {
            const next = { ...habit };
            if (event.target.value) next.areaID = event.target.value; else delete next.areaID;
            model.updateHabit(next, "Moved");
          }}>
          <option value="">{t("No Area")}</option>
          {state.lists.sortedAreas.map((one) => <option key={one.id} value={one.id}>{[splitEmoji(one.title).emoji, splitEmoji(one.title).name].filter(Boolean).join(" ")}</option>)}
        </select>
        {currentPause(habit, today) && <span className="chip static"><Icon name="pause" />{t("Paused until {day}", { day: shortDate(currentPause(habit, today)!.until ?? today, today) })}</span>}
        {area === undefined && habit.areaID !== undefined && null}
      </div>
      {model.restsHabitToday(habit.id) && (
        // Off today with Not Today: it says so, with the way back (Bastian, 2026-10-30).
        <div className="habit-page-rests">
          <NotTodayLabel />
          <button className="outline-button small" onClick={() => unrestToday(model, habit.id, window.showToast)}>{t("Back to Today")}</button>
        </div>
      )}
      <div className="habit-tiles">
        <div className="habit-tile lilac"><b>{progress.streak}</b><span>{unitText(unit, "inARow")}</span></div>
        <div className="habit-tile"><b>{progress.best}</b><span>{t("best so far")}</span></div>
        {unit === "day" ? (
          <div className="habit-tile"><b>{doneInMonth}<small> {t("of {count}", { count: Math.max(planned, doneInMonth) })}</small></b>
            <span>{t("days in {month}", { month: formatDay(today, { month: "long" }) })}</span></div>
        ) : (
          <div className="habit-tile"><b>{progress.periodDone}<small> {t("of {count}", { count: progress.periodTarget })}</small></b>
            <span>{unit === "week" ? t("this week") : t("this month")}</span></div>
        )}
      </div>
      <div className="habit-history">
        <section>
          <h2>{t("Since {month}", { month: formatDay(habitStart(habit, state.zone), { month: "long" }) })}</h2>
          {unit === "day" ? <DayGrid habit={habit} steps={steps} today={today} /> : <PeriodDots steps={steps} today={today} unit={unit} />}
          <div className="habit-legend">
            <span><i className="habit-dot done" />{t("Done")}</span>
            <span><i className="habit-dot kept" />{unit === "day" ? t("Missed once, kept") : t("Short, kept")}</span>
            <span><i className="habit-dot missed" />{unit === "day" ? t("Missed") : t("Short")}</span>
          </div>
        </section>
        <section>
          <div className="habit-month-head">
            <button className="small-button" aria-label={t("Earlier")} onClick={() => setMonth(addDays(month, -1).slice(0, 8).concat("01") as Day)}><Icon name="chevLeft" /></button>
            <h2>{formatDay(month, { month: "long", year: month.slice(0, 4) === today.slice(0, 4) ? undefined : "numeric" })}</h2>
            <button className="small-button" aria-label={t("Later")} disabled={month >= `${today.slice(0, 8)}01`}
              onClick={() => setMonth(addDays(month, 32).slice(0, 8).concat("01") as Day)}><Icon name="chevRight" /></button>
            <span className="habit-hint">{t("Click a day to change it")}</span>
          </div>
          <MonthCalendar habit={habit} month={month} steps={steps} done={done} today={today}
            onToggle={(day, on) => { model.setHabitDay(habit.id, day, on); if (on && day === today) habitTicked(window, todayTodo ? [todayTodo] : []); }} />
        </section>
      </div>
      <textarea className="notes habit-notes" value={notes} placeholder={t("Notes")} aria-label={t("Notes")} onChange={(event) => setNotes(event.target.value)} onBlur={save} />
      <div className="habit-keys">
        <span><kbd className="kbd">←</kbd> <kbd className="kbd">Esc</kbd> {t("Back to the list")}</span>
        <span><kbd className="kbd">Space</kbd> {t("Complete today")}</span>
      </div>
    </div>
  );
}

function unitText(unit: HabitUnit, what: "inARow"): string {
  void what;
  return unit === "day" ? t("days in a row") : unit === "week" ? t("weeks in a row") : t("months in a row");
}

function HowOftenChip({ habit }: { habit: Habit }) {
  const { model } = useWindow();
  const [open, setOpen] = useState(false);
  const anchor = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button ref={anchor} className="chip" onClick={() => setOpen(true)} title={t("How Often")}>
        <Icon name="repeat" />{scheduleLabel(habit.schedule)}{(habit.neverMissTwice ?? true) && ` · ${t("never miss twice")}`}
      </button>
      {open && <HowOftenPopover habit={habit} anchor={anchor} onClose={() => setOpen(false)} />}
    </>
  );
}

/** Every day since it began, a column a week (board 22): done lilac, kept dashed, missed grey. */
function DayGrid({ habit, steps, today }: { habit: Habit; steps: readonly HabitStep[]; today: Day }) {
  const { state } = useWindow();
  const byDay = new Map(steps.map((step) => [step.first, step.state]));
  const start = habitStart(habit, state.zone);
  const first = weekStart(addDays(today, -7 * 25) > start ? addDays(today, -7 * 25) : start, 2);
  const weeks: Day[][] = [];
  for (let monday = first; monday <= today; monday = addDays(monday, 7)) weeks.push(Array.from({ length: 7 }, (_, index) => addDays(monday, index)));
  return (
    <div className="habit-grid" role="img" aria-label={t("The days since it began")}>
      {weeks.map((week) => (
        <div key={week[0]} className="habit-grid-week">
          {week.map((day) => {
            const state = day < start ? "before" : day > today ? "future" : byDay.get(day) ?? "none";
            return <i key={day} className={`habit-cell ${state}${day === today ? " ring" : ""}`} title={formatDay(day, { day: "numeric", month: "short" })} />;
          })}
        </div>
      ))}
    </div>
  );
}

/** A dot a week (or a month) since it began. */
function PeriodDots({ steps, today, unit }: { steps: readonly HabitStep[]; today: Day; unit: HabitUnit }) {
  return (
    <div className="habit-dots large" role="img" aria-label={unit === "week" ? t("Weeks") : t("Months")}>
      {steps.slice(-26).map((step) => <span key={step.first} className={`habit-dot ${step.state}`} title={shortDate(step.first, today)} />)}
    </div>
  );
}

/** The month, Monday first; a past day clicked is done or not (the user's own record). */
function MonthCalendar({ habit, month, steps, done, today, onToggle }: {
  habit: Habit; month: Day; steps: readonly HabitStep[]; done: ReadonlySet<Day>; today: Day; onToggle: (day: Day, done: boolean) => void;
}) {
  const { state } = useWindow();
  const start = habitStart(habit, state.zone);
  const byDay = new Map(steps.filter((step) => step.first === step.last).map((step) => [step.first, step.state]));
  const length = new Date(Date.UTC(dayParts(month).year, dayParts(month).month, 0)).getUTCDate();
  const lead = (weekday(month) - 2 + 7) % 7;
  const flexible = isFlexible(habit.schedule);
  return (
    <div className="habit-calendar">
      {WEEK.map((day) => <span key={day} className="habit-calendar-name">{weekdayName(day, "narrow")}</span>)}
      {Array.from({ length: lead }, (_, index) => <span key={`lead-${index}`} />)}
      {Array.from({ length }, (_, index) => {
        const day = addDays(month, index);
        const isDone = done.has(day);
        const kind = day > today ? "future" : isDone ? "done" : flexible ? (day < start ? "before" : "open") : byDay.get(day) ?? "none";
        return (
          <button key={day} className={`habit-calendar-day ${kind}${day === today ? " ring" : ""}`} disabled={day > today}
            aria-pressed={isDone} aria-label={formatDay(day, { weekday: "long", day: "numeric", month: "long" })}
            onClick={() => onToggle(day, !isDone)}>
            {dayParts(day).date}
          </button>
        );
      })}
    </div>
  );
}

// MARK: - Habits (Today | All)

/** A row of Habits' lists: the habit, the day it stands for today (`habitCardDay`), and whether it counts as done. */
interface HabitRow { habitID: string; day: Todo; done: boolean }

/** The open card's row keeps the group it had when the card opened (the kit's `held`), until the card closes. */
let held: { id: string; done: boolean } | null = null;

/**
 * Whether a row counts as done in Habits' lists (`habitsDoneLast`): today's day ticked, and no longer lingering, so one
 * just ticked keeps its place for its moment. With `open` (the window's open card), that row stays in its group.
 */
function doneInList(state: ModelState, day: Todo, open?: string | null): boolean {
  const done = day.completedAt !== undefined && !state.lists.isLingering(day.id);
  if (open === undefined) return done;
  if (held && held.id !== open) held = null;
  if (day.id !== open) return done;
  held ??= { id: day.id, done };
  return held.done;
}

/** Habits with a day today, as rows: the open ones first, then those done today, each in the user's order. */
function habitRows(state: ModelState, model: Model, habits: readonly Habit[], open?: string | null): (HabitRow & { habit: Habit })[] {
  return habitsDoneLast(habits.flatMap((habit) => {
    const day = model.habitCardDay(habit.id);
    return day ? [{ habit, habitID: habit.id, day, done: doneInList(state, day, open) }] : [];
  }), (row) => row.done);
}

/** What Habits shows: today's days (the just ticked kept a moment), and all of them, Paused apart; done today after
 *  the open ones (Bastian, 2026-10-29). A habit taken off today (Not Today) or not due is listed under All only
 *  (Bastian, 2026-10-28). */
function habitsRows(state: ModelState, model: Model, open?: string | null) {
  const habits = sortedHabits(state.records.habits);
  const running = habitRows(state, model, habits.filter((habit) => !currentPause(habit, state.today)), open);
  const paused = habits.filter((habit) => currentPause(habit, state.today));
  const todayTodos = state.lists.todayTodos("today").filter((todo) => todo.habitID !== undefined)
    .sort((a, b) => (habits.findIndex((habit) => habit.id === a.habitID) - habits.findIndex((habit) => habit.id === b.habitID)));
  const lingering = state.records.todos.filter((todo) => todo.habitID !== undefined && state.lists.isLingering(todo.id) && !todayTodos.includes(todo));
  const todays: HabitRow[] = habitsDoneLast([...todayTodos, ...lingering].map((day) => ({ habitID: day.habitID!, day, done: doneInList(state, day, open) })),
    (row) => row.done);
  return { habits, running, paused, todays };
}

/** An area's habits, open first, then done today. */
function areaHabitRows(state: ModelState, model: Model, areaID: string, open?: string | null) {
  return habitRows(state, model, sortedHabits(state.records.habits).filter((habit) => habit.areaID === areaID), open);
}

/** The habits' rows in Habits and at the top of an area, top to bottom, for the window's ↑ ↓ and selection (Window.tsx). */
export function habitRowIDs(ui: Pick<UI, "route" | "habitsFilter" | "finding" | "query">, state: ModelState, model: Model): string[] {
  const dayIDs = (habits: readonly Habit[]) => habits.flatMap((habit) => model.habitCardDay(habit.id)?.id ?? []);
  if (ui.finding && ui.query.trim() !== "") return [];
  if (ui.route.kind === "habits") {
    const rows = habitsRows(state, model);
    if (ui.habitsFilter === "all") return [...rows.running.map((row) => row.day.id), ...dayIDs(rows.paused)];
    return rows.todays.map((row) => row.day.id);
  }
  if (ui.route.kind === "area") return areaHabitRows(state, model, ui.route.id).map((row) => row.day.id);
  return [];
}

/**
 * Habits' rows as places to drop a habit on, with the lists' own marks, lift and touch drag (dnd.ts; Bastian,
 * 2026-10-29): above a row, or below it, which is above the next of its group or, below the last, right after it in the
 * user's order (moveHabitAfter). A habit lands only among its own group, the open ones or those done today.
 */
function habitDrops(model: Model, rows: readonly HabitRow[]): Map<string, ReturnType<typeof columnRowDropProps>> {
  const drops = new Map<string, ReturnType<typeof columnRowDropProps>>();
  rows.forEach((row, index) => {
    const group = rows.filter((other) => other.done === row.done).map((other) => other.day.id);
    const following = rows[index + 1];
    const next = following && following.done === row.done ? following.day.id : undefined;
    const takes = () => {
      const ids = draggedIDs();
      return ids !== null && ids.length > 0 && ids.every((id) => group.includes(id));
    };
    const land = (ids: readonly string[], before: string | undefined) => {
      // In the order shown, so a selection keeps its own order.
      const moving = rows.filter((other) => ids.includes(other.day.id)).map((other) => other.habitID);
      const target = before === undefined || ids.includes(before) ? undefined : rows.find((other) => other.day.id === before)?.habitID;
      if (target !== undefined) {
        for (const id of moving) model.moveHabit(id, target);
        return;
      }
      let after = row.habitID;
      for (const id of moving) {
        model.moveHabitAfter(id, after);
        after = id;
      }
    };
    drops.set(row.day.id, columnRowDropProps(row.day, next, land, takes));
  });
  return drops;
}

/** The habit a row stands for: its stored day's, or the one whose day today, not stored, it is (`habitCardDay`). */
export function habitOfRow(state: ModelState, id: string | undefined): string | undefined {
  if (id === undefined) return undefined;
  return state.records.todos.find((todo) => todo.id === id)?.habitID
    ?? state.records.habits.find((habit) => habitTodoID(habit.id, state.today) === id)?.id;
}

/** The habits' own page (boards 11, 12): Today's to tick, or all of them in the user's order, Paused at the end. */
export function HabitsPage() {
  const window = useWindow();
  const { state, model, ui, set } = window;
  const filter = ui.habitsFilter;
  const { habits, running, paused, todays } = habitsRows(state, model, ui.open);
  const complete = (ids: readonly string[]) => {
    const todos = state.records.todos.filter((todo) => ids.includes(todo.id));
    const completing = todos.filter((todo) => todo.completedAt === undefined);
    tickAskingFirst(window, completing, () => {
      if (completing.length) tickSound(completing);
      model.toggleCompletion(ids);
      if (completing.length) { window.showToast(t("Completed"), true); habitTicked(window, completing); }
    });
  };
  const line = (habit: Habit, drop?: ReturnType<typeof columnRowDropProps>) => {
    const day = model.habitCardDay(habit.id);
    if (!day) return null;
    return ui.open === day.id
      ? <HabitCardInList key={habit.id} todo={day} />
      : <HabitLine key={habit.id} habit={habit} day={day} onComplete={complete} card {...(drop ? { drop } : {})} />;
  };
  const drops = habitDrops(model, filter === "all" ? running : todays);
  return (
    <div className="habits-page">
      {/* The lists' header (the display decisions of 2026-10-29, points 1 B, 10 B, 12 E): Today | All on the title's line
          in the lists' switch, no date; New Habit is in ···. */}
      <ListHeader icon={<span className="icon-square habits"><Icon name="flame" /></span>} title={t("Habits")}
        controls={<ViewSwitch label={t("Show")} value={filter} onChange={(value) => set({ habitsFilter: value, selected: [] })}
          options={[{ value: "today", label: t("Today") }, { value: "all", label: t("All") }]} />} />
      <MadeTodoCard />
      {habits.length === 0 ? (
        <div className="empty">
          <span className="icon-square habits"><Icon name="flame" /></span>
          <b>{t("No habits yet")}</b>
          {t("A habit is a to-do you mean to do again and again. Make one here, or with the flame in any to-do.")}
        </div>
      ) : filter === "today" ? (
        <>
          {/* Each habit a full-width card, in one column (the Habits page E2, 2026-10-29); Today's list keeps rows. */}
          <div className="habit-lines habit-panels" role="listbox" aria-label={t("Habits Today")}>
            {todays.map(({ day: todo, habitID }) => {
              const habit = state.records.habits.find((other) => other.id === habitID);
              if (ui.open === todo.id || !habit) return <HabitCardInList key={todo.id} todo={todo} />;
              const drop = drops.get(todo.id);
              return <HabitLine key={todo.id} habit={habit} day={todo} onComplete={complete} card {...(drop ? { drop } : {})} />;
            })}
          </div>
          {todays.length === 0 && <div className="empty"><b>{t("Nothing for today")}</b>{t("Your habits for today are done, or none is due.")}</div>}
        </>
      ) : (
        <div className="habit-lines habit-panels" role="listbox" aria-label={t("Habits")}>
          {running.map((row) => line(row.habit, drops.get(row.day.id)))}
          {paused.length > 0 && <h2 className="habits-paused-title">{t("Paused")}<span className="section-count">{paused.length}</span></h2>}
          {paused.map((habit) => line(habit))}
        </div>
      )}
    </div>
  );
}

/**
 * The to-do Make a To-Do made on a day off, open where its habit was: it starts on the habit's next planned day, so
 * this list doesn't show it (it waits in Scheduled), and once its card closes it is gone from here.
 */
function MadeTodoCard() {
  const { ui, state, visibleIDs } = useWindow();
  const open = ui.open;
  const todo = open === null || visibleIDs.includes(open) ? undefined : state.records.todos.find((one) => one.id === open && one.habitID === undefined);
  return todo ? <HabitCardInList key={todo.id} todo={todo} /> : null;
}

/** A habit day's open card inside Habits and an area's habits (the list's own card, as in Today). */
function HabitCardInList({ todo }: { todo: Todo }) {
  // The card lives in TodoCard; imported lazily to keep the modules apart.
  const Card = cardComponent.current;
  return Card ? <Card todo={todo} /> : null;
}

/** Set by TodoCard.tsx, which imports this module (a cycle the other way round would load the card too early). */
export const cardComponent: { current: ((props: { todo: Todo }) => ReactNode) | null } = { current: null };

/** One day of a habit's card strip: done, kept (dashed), missed, today (its ring), coming, or off (not one of its days). */
interface StripDay { day: Day; state: "done" | "kept" | "missed" | "today" | "coming" | "off" }

/** The last `count` weeks of a habit, Monday first, a cell a day (Habits' cards, E2): its own days by their state. */
function habitStrip(habit: Habit, done: ReadonlySet<Day>, today: Day, zone: string, count: number): StripDay[][] {
  const steps = habitSteps(habit, done, today, zone);
  const byDay = new Map(steps.filter((step) => step.first === step.last).map((step) => [step.first, step.state]));
  const start = habitStart(habit, zone);
  const from = scheduleStart(habit, zone);
  const flexible = isFlexible(habit.schedule);
  const first = addDays(weekStart(today, 2), -7 * (count - 1));
  const state = (day: Day): StripDay["state"] => {
    if (day < start) return "off";
    const planned = flexible || isScheduled(habit.schedule, day, from);
    if (day > today) return planned && !isPaused(habit, day) ? "coming" : "off";
    if (done.has(day)) return "done";
    if (day === today) return planned && !isPaused(habit, day) ? "today" : "off";
    if (flexible) return "off";
    const step = byDay.get(day);
    return step === "kept" ? "kept" : step === "missed" ? "missed" : "off";
  };
  return Array.from({ length: count }, (_, week) => Array.from({ length: 7 }, (_, index) => {
    const day = addDays(first, week * 7 + index);
    return { day, state: state(day) };
  }));
}

/**
 * One habit in All, and an area's habit without a day today: its box, its title, area and How Often,
 * the last 7 days or weeks, and its badge. It behaves as a to-do's row: a click selects, a double-click or Return opens
 * the card on `day` (today's, or on a day it isn't due, one not stored). Not due today, the box is quiet and asks before
 * counting the day anyway; paused, the pause sign takes its place. With `drop` (not paused) it is dragged as a to-do's
 * row is, with the mouse and, held, with a finger (dnd.ts, touch.ts).
 */
function HabitLine({ habit, day, onComplete, drop, card = false }: {
  habit: Habit; day: Todo; onComplete: (ids: readonly string[]) => void; drop?: ReturnType<typeof columnRowDropProps>;
  /** Habits' own page (E2, 2026-10-29): a full-width card with the last weeks, the status and best/next. */
  card?: boolean;
}) {
  const window = useWindow();
  const { state, model, ui, layout, select, openTodo } = window;
  const prefs = usePrefs();
  const progress = model.habitProgress(habit);
  const badge = habitBadge(progress, prefs.showsStreaks);
  const steps = habitSteps(habit, model.habitDoneDays(habit.id) as Set<Day>, state.today, state.zone).slice(-7);
  const area = state.lists.area(habit.areaID);
  const pause = currentPause(habit, state.today);
  const unplanned = model.isUnstoredHabitDay(day);
  const done = day.completedAt !== undefined;
  // Area · how often · the week's count, in the details' grey; "missed yesterday" alone in lilac, until the day is done
  // (the display decisions of 2026-10-29, point 2 C).
  const status = pause ? undefined : habitStatus(badge, done);
  // Off today with Not Today, it says so (Bastian, 2026-10-30); on the row in How Often's place, as a break does.
  const rests = model.restsHabitToday(habit.id);
  const line = [area ? splitEmoji(area.title).name : undefined, pause ? t("Until {day} · streak kept", { day: shortDate(pause.until ?? state.today, state.today) }) : rests ? undefined : scheduleLabel(habit.schedule)]
    .filter(Boolean).join(" · ");
  // Habits' card: the week done says so in a lilac capsule in place of "4 of 4 this week", for the rest of the week
  // (board 11, Bastian 2026-10-31); the tick that closes it lights the card up for a moment.
  const closed = card && !pause ? periodDone(progress) : undefined;
  const wasClosed = useRef(closed);
  const [glows, setGlows] = useState(false);
  useEffect(() => {
    const before = wasClosed.current;
    wasClosed.current = closed;
    if (!closed || before || reducesMotion()) return;
    setGlows(true);
    const timer = setTimeout(() => setGlows(false), 1200);
    return () => { clearTimeout(timer); setGlows(false); };
  }, [closed]);
  const selected = ui.selected.includes(day.id);
  const lingering = done && state.lists.isLingering(day.id);
  const click = (event: MouseEvent) => {
    if ((event.target as HTMLElement).closest("button")) return;
    if (ui.open !== null || ui.composing !== null) {
      document.dispatchEvent(new CustomEvent("todolix:close-card"));
      return;
    }
    if (layout.touch && !event.metaKey && !event.ctrlKey && !event.shiftKey) return openTodo(day.id);
    select(day.id, { toggle: event.metaKey || event.ctrlKey, range: event.shiftKey });
  };
  const key = (event: KeyboardEvent) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === "Enter") { event.preventDefault(); event.stopPropagation(); openTodo(day.id); }
  };
  const rowMenu = useHabitRowMenu(habit, day, onComplete);
  // A hold lifts it under a finger; it doesn't swipe (TodoRow's swipes are a to-do's When and Move).
  const element = useRef<HTMLDivElement>(null);
  useRowTouch(element, {
    enabled: layout.touch && drop !== undefined && ui.open === null && ui.composing === null,
    canDrag: true,
    swipes: false,
    dragIDs: () => (ui.selected.includes(day.id) ? ui.selected : [day.id]),
    onSwipeState: () => {},
    onSwipe: () => {},
  });
  if (card) {
    const where = [area ? splitEmoji(area.title).name : undefined, scheduleLabel(habit.schedule)].filter(Boolean).join(" · ");
    const statusText = pause ? t("Until {day} · streak kept", { day: shortDate(pause.until ?? state.today, state.today) }) : badgeDetail(status);
    const next = pause ? undefined : nextPlannedDay(habit, state.today, state.zone);
    // Quiet, at the status line's right: the best so far (left out while the habit is at it: its pill says so) and,
    // for set weekdays, the next one.
    const quiet = [
      !pause && progress.best > progress.streak ? t("Best so far: {best}", { best: progress.best }) : undefined,
      next ? t("Next: {day}", { day: formatDay(next, { weekday: "short" }) }) : undefined,
    ].filter(Boolean).join(" · ");
    const weeks = habitStrip(habit, model.habitDoneDays(habit.id) as Set<Day>, state.today, state.zone, layout.narrow ? 3 : 6);
    return (
      <>
      <div ref={element} className={`row habit habit-line habit-panel${glows ? " week-glow" : ""}${pause ? " paused" : ""}${done ? " done" : ""}${lingering ? " lingering" : ""}${lingering && tickedInCard.has(day.id) ? " quiet-tick" : ""}${selected ? " selected" : ""}`}
        role="option" aria-selected={selected} tabIndex={selected || (ui.selected.length === 0 && window.visibleIDs[0] === day.id) ? 0 : -1}
        data-row={day.id} onClick={click} onKeyDown={key} onContextMenu={rowMenu.onContextMenu} aria-label={habit.title}
        {...(drop ? rowDragProps(window, day) : {})} {...drop}
        onDoubleClick={(event) => { if (!(event.target as HTMLElement).closest("button") && !layout.touch) openTodo(day.id); }}>
        <div className="habit-panel-top">
          {pause ? (
            <span className="habit-pause-sign" role="img" aria-label={t("Paused")}><Icon name="pause" /></span>
          ) : (
            <button className={`check tickbox${unplanned ? " not-planned" : ""}`} aria-label={done ? t("Mark as Not Done") : t("Complete")} aria-pressed={done}
              onClick={(event) => { event.stopPropagation(); if (unplanned) askCountAnyway(window, day); else onComplete([day.id]); }}>
              <span className="tick-ring" aria-hidden="true" />
              <span className="fill" aria-hidden="true" />
              <Icon name="check" className="hint" />
              <svg className="tick" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path pathLength={1} d="M5.6 10.4 8.7 13.4 14.4 7.2" /></svg>
            </button>
          )}
          <div className="habit-panel-text">
            <span className="title">{habit.title || <span className="untitled">{t("New Habit")}</span>}</span>
            <span className="where">{where}</span>
          </div>
          {!pause && <HabitBadgeView badge={badge} progress={progress} />}
        </div>
        <div className="habit-panel-body">
          <div className="habit-strip" aria-hidden="true" style={{ gridTemplateColumns: `repeat(${weeks.length}, minmax(0, 1fr))` }}>
            {weeks.map((week) => (
              <div key={week[0]!.day} className="habit-strip-week">
                {week.map((cell) => <i key={cell.day} className={`habit-cell-day ${cell.state}`} title={formatDay(cell.day, { weekday: "short", day: "numeric", month: "short" })} />)}
              </div>
            ))}
          </div>
          {(statusText || quiet) && (
            <div className="habit-panel-status">
              {closed && !rests ? (
                <span className="week-done-capsule">
                  <span className="week-done-dots" aria-hidden="true">{Array.from({ length: Math.min(progress.periodTarget, 7) }, (_, index) => <i key={index} />)}</span>
                  {closed === "month" ? t("Month done") : t("Week done")}
                </span>
              ) : (
                <span className={status?.kind === "missed" && !pause ? "missed" : ""}>{rests && <><NotTodayLabel /> · </>}{statusText}</span>
              )}
              {quiet && <span className="habit-panel-quiet">{quiet}</span>}
            </div>
          )}
        </div>
      </div>
      {rowMenu.menu}
      </>
    );
  }
  return (
    <>
    <div ref={element} className={`row habit habit-line${pause ? " paused" : ""}${done ? " done" : ""}${lingering ? " lingering" : ""}${lingering && tickedInCard.has(day.id) ? " quiet-tick" : ""}${selected ? " selected" : ""}`}
      role="option" aria-selected={selected} tabIndex={selected || (ui.selected.length === 0 && window.visibleIDs[0] === day.id) ? 0 : -1}
      data-row={day.id} onClick={click} onKeyDown={key} onContextMenu={rowMenu.onContextMenu} aria-label={habit.title}
      {...(drop ? rowDragProps(window, day) : {})} {...drop}
      onDoubleClick={(event) => { if (!(event.target as HTMLElement).closest("button") && !layout.touch) openTodo(day.id); }}>
      {pause ? (
        <span className="habit-pause-sign" role="img" aria-label={t("Paused")}><Icon name="pause" /></span>
      ) : (
        <button className={`check tickbox${unplanned ? " not-planned" : ""}`} aria-label={done ? t("Mark as Not Done") : t("Complete")} aria-pressed={done}
          onClick={(event) => { event.stopPropagation(); if (unplanned) askCountAnyway(window, day); else onComplete([day.id]); }}>
          <span className="tick-ring" aria-hidden="true" />
          <span className="fill" aria-hidden="true" />
          <Icon name="check" className="hint" />
          <svg className="tick" viewBox="0 0 20 20" aria-hidden="true" focusable="false"><path pathLength={1} d="M5.6 10.4 8.7 13.4 14.4 7.2" /></svg>
        </button>
      )}
      <div className="habit-line-text">
        <span className="title">{habit.title}</span>
        <span className="where">
          <span className="habit-meta">
            {line}
            {rests && <>{line && " · "}<NotTodayLabel /></>}
            {status && <span className={`habit-detail${status.kind === "missed" ? " missed" : ""}`}> · {badgeDetail(status)}</span>}
          </span>
        </span>
      </div>
      <div className="habit-line-side">
        {!pause && <HabitBadgeView badge={badge} progress={progress} />}
        {!pause && (
          <span className={`habit-dots small${isFlexible(habit.schedule) ? " weeks" : ""}`} aria-hidden="true">
            {steps.map((step) => <i key={step.first} className={`habit-dot ${step.state}`} />)}
          </span>
        )}
      </div>
    </div>
    {rowMenu.menu}
    </>
  );
}

/**
 * New in Habits, "+ Habit" in an area: three free, then Pro. A habit without a name, every day (so it has today's
 * to-do), whose card opens with the cursor in the title, as a new to-do's; closed without a name, it goes.
 */
export function openNewHabit(window: ReturnType<typeof useWindow>, areaID?: string) {
  const running = sortedHabits(window.state.records.habits).length;
  if (!window.state.isPro && running >= 3) return window.showSheet({ kind: "pro", feature: "habits" });
  const habit = window.model.addHabit("", { schedule: { kind: "everyNDays", interval: 1 }, ...(areaID ? { areaID } : {}) });
  const day = habit ? window.model.habitCardDay(habit.id) : undefined;
  if (day) window.openTodo(day.id);
}

// MARK: - An area's habits

/** An area's page: its habits in a lilac group at the top, "+ Habit" in it (board 13). */
export function AreaHabits({ areaID, route }: { areaID: string; route: Route }) {
  const window = useWindow();
  const { state, model, ui } = window;
  const habits = areaHabitRows(state, model, areaID, ui.open);
  if (habits.length === 0) return null;
  const drops = habitDrops(model, habits);
  const complete = (ids: readonly string[]) => {
    const todos = state.records.todos.filter((todo) => ids.includes(todo.id));
    const completing = todos.filter((todo) => todo.completedAt === undefined);
    tickAskingFirst(window, completing, () => {
      if (completing.length) tickSound(completing);
      model.toggleCompletion(ids);
      if (completing.length) { window.showToast(t("Completed"), true); habitTicked(window, completing); }
    });
  };
  return (
    <div className="group habit-group area-habits">
      <div className="text-head habit-group-head">
        <Icon name="flame" />{t("Habits")}<span className="section-count">{habits.length}</span>
        <span className="spacer" />
        <button className="link" onClick={() => openNewHabit(window, areaID)}>+ {t("Habit")}</button>
      </div>
      <MadeTodoCard />
      {habits.map(({ habit, day }) => {
        if (ui.open === day.id) return <HabitCardInList key={habit.id} todo={day} />;
        const drop = currentPause(habit, state.today) ? undefined : drops.get(day.id);
        return model.isUnstoredHabitDay(day)
          ? <HabitLine key={habit.id} habit={habit} day={day} onComplete={complete} {...(drop ? { drop } : {})} />
          : <TodoRow key={habit.id} todo={day} route={route} showsPlace={false} inToday={false} onComplete={complete} drop={drop} />;
      })}
    </div>
  );
}

// MARK: - Sheets

export function PauseHabitSheet({ habitID, onClose }: { habitID: string; onClose: () => void }) {
  const { state, model, showToast } = useWindow();
  const [until, setUntil] = useState<string>(addDays(state.today, 6));
  const pause = () => {
    if (until < state.today) return;
    model.pauseHabit(habitID, until as Day);
    showToast(t("Habit paused"), true);
    onClose();
  };
  return (
    <Dialog onClose={onClose}>
      <h2 id="sheet-title">{t("Pause Habit")}</h2>
      <p>{t("For a holiday or an illness: nothing is due, and the streak waits.")}</p>
      <div className="segmented" role="radiogroup" aria-label={t("Until")}>
        {[[6, t("A Week")], [13, t("Two Weeks")], [29, t("A Month")]].map(([days, label]) => {
          const day = addDays(state.today, days as number);
          return <button key={day} role="radio" aria-checked={until === day} className={until === day ? "on" : ""} onClick={() => setUntil(day)}>{label}</button>;
        })}
      </div>
      <label className="confirm-field">
        <span>{t("Until")}</span>
        <input className="field" type="date" value={until} min={state.today} onChange={(event) => setUntil(event.target.value)} aria-label={t("Until")} />
      </label>
      <div className="actions">
        <button className="outline-button" onClick={onClose}>{t("Cancel")}</button>
        <button className="mint-button" onClick={pause}>{t("Pause")}</button>
      </div>
    </Dialog>
  );
}

/**
 * A milestone, right after the tick, once per milestone and habit (docs/63, Milestones; board M1, Bastian 2026-10-31):
 * a card in the moment's dark lilac over the dimmed window, as on the Mac (on a phone it fills the screen, as on the
 * iPhone). Confetti falls briefly, the flame springs in, the number, the habit, its line and the path of milestones.
 * No button: a click anywhere, Esc or Return closes it, or it closes by itself after a moment (not while the pointer
 * rests on it). Reduce Motion: no confetti and no spring, it fades in and out. `?demo&milestoneStill` keeps it, the
 * confetti still, for pictures.
 */
export function MilestoneMoment({ habitID, count, onClose }: { habitID: string; count: number; onClose: () => void }) {
  const { state, model, layout } = useWindow();
  const habit = state.records.habits.find((other) => other.id === habitID);
  const [closing, setClosing] = useState(false);
  const panel = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const stays = import.meta.env.DEV && typeof location !== "undefined" && new URLSearchParams(location.search).has("milestoneStill");
  const close = useCallback(() => setClosing(true), []);
  useEffect(() => {
    model.noteMilestoneShown(habitID, count);
  }, [habitID, count, model]);
  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    panel.current?.focus();
    return () => returnTo?.focus?.({ preventScroll: true });
  }, []);
  const wait = useCallback(() => {
    clearTimeout(timer.current);
    if (!stays) timer.current = setTimeout(close, MILESTONE_SHOWS_MS);
  }, [close, stays]);
  useEffect(() => {
    wait();
    return () => clearTimeout(timer.current);
  }, [wait]);
  useEffect(() => {
    if (!closing) return;
    const done = setTimeout(onClose, 250);
    return () => clearTimeout(done);
  }, [closing, onClose]);
  if (!habit) return null;
  const unit = unitOf(habit.schedule);
  const key = (event: KeyboardEvent) => {
    if (["Escape", "Enter", " "].includes(event.key)) {
      event.preventDefault();
      event.stopPropagation();
      close();
    } else if (event.key === "Tab") {
      event.preventDefault();
    }
  };
  const steps = MILESTONES[unit];
  // On the page itself, above the window's toast ("Completed · Undo") and everything else.
  return createPortal(
    <div className={`milestone-moment${closing ? " closing" : ""}${stays ? " still" : ""}`} onMouseDown={close} onKeyDown={key}>
      <div ref={panel} className="milestone-card" role="dialog" aria-modal="true" tabIndex={-1}
        aria-label={`${count} ${unitText(unit, "inARow")}, ${habit.title}`} aria-describedby="milestone-line"
        onMouseEnter={() => clearTimeout(timer.current)} onMouseLeave={wait}>
        <div className="milestone-confetti" aria-hidden="true">
          {CONFETTI.map((piece, index) => (
            <i key={index} className={piece.round ? "round" : undefined} style={{
              left: `${(piece.x / 402) * 100}%`, width: piece.width, height: piece.height, background: piece.color,
              "--from": `${(piece.y / 874 - piece.speed * 0.9) * 100}%`, "--to": `${(piece.y / 874 + piece.speed * 1.7) * 100}%`,
              "--turn-from": `${piece.angle - piece.spin * 0.9}deg`, "--turn-to": `${piece.angle + piece.spin * 1.7}deg`,
            } as CSSProperties} />
          ))}
        </div>
        <div className="milestone-body">
          <span className="milestone-flame"><Icon name="flameFill" /></span>
          <b className="milestone-number">{count}</b>
          <span className="milestone-unit">{unitText(unit, "inARow")}</span>
          <span className="milestone-title">{habit.title}</span>
          <span className="milestone-line" id="milestone-line">{milestoneLine(count, unit)}</span>
          <div className="milestone-path" aria-hidden="true">
            {steps.map((step, index) => (
              <Fragment key={step}>
                {index > 0 && <span className={`milestone-link${step <= count ? " on" : ""}`} />}
                <span className={`milestone-step${step === count ? " now" : step < count ? " on" : ""}`}><i />{step}</span>
              </Fragment>
            ))}
          </div>
        </div>
        <span className="milestone-hint" aria-hidden="true">{layout.touch ? t("Tap anywhere") : t("Click anywhere")}</span>
      </div>
    </div>,
    document.body,
  );
}

/** How long the moment stays (the kit's `MilestoneMoment.duration`). */
const MILESTONE_SHOWS_MS = 2800;

/** The line under the habit (the kit's `MilestoneMoment.line`). */
function milestoneLine(count: number, unit: HabitUnit): string {
  switch (milestoneSpan(count, unit)) {
    case "week": return t("A whole week of it, as you planned it.");
    case "month": return t("A month of it, as you planned it.");
    case "overThreeMonths": return t("Over three months of it, as you planned it.");
    case "season": return t("A season of it, as you planned it.");
    case "halfYear": return t("Half a year of it, as you planned it.");
    case "year": return t("A whole year of it, as you planned it.");
    default: return t("In a row, as you planned it. Keep it going.");
  }
}

/** The pieces of confetti where the board has them (402 × 874) at 0.9 s of their fall (the kit's `MilestoneConfetti`). */
const CONFETTI: { x: number; y: number; width: number; height: number; color: string; angle: number; round?: boolean; speed: number; spin: number }[] = [
  { x: 50, y: 130, width: 8, height: 14, color: "#9A7DFF", angle: 20, speed: 0.2, spin: 90 },
  { x: 120, y: 90, width: 7, height: 7, color: "#1ADB9E", angle: 0, round: true, speed: 0.17, spin: 0 },
  { x: 310, y: 120, width: 8, height: 14, color: "#1ADB9E", angle: -30, speed: 0.22, spin: -120 },
  { x: 350, y: 210, width: 7, height: 7, color: "#C4B2FF", angle: 0, round: true, speed: 0.15, spin: 0 },
  { x: 30, y: 260, width: 6, height: 6, color: "#C4B2FF", angle: 0, round: true, speed: 0.18, spin: 0 },
  { x: 260, y: 70, width: 6, height: 11, color: "#F2C94C", angle: 45, speed: 0.19, spin: 140 },
  { x: 340, y: 160, width: 6, height: 6, color: "#FFFFFF", angle: 0, round: true, speed: 0.16, spin: 0 },
  { x: 360, y: 330, width: 6, height: 11, color: "#9A7DFF", angle: 10, speed: 0.21, spin: -90 },
  { x: 22, y: 400, width: 8, height: 13, color: "#1ADB9E", angle: -15, speed: 0.23, spin: 110 },
  { x: 80, y: 200, width: 5, height: 9, color: "#F2C94C", angle: -40, speed: 0.18, spin: -150 },
  { x: 330, y: 450, width: 7, height: 7, color: "#1ADB9E", angle: 0, round: true, speed: 0.2, spin: 0 },
  { x: 220, y: 40, width: 7, height: 12, color: "#9A7DFF", angle: -20, speed: 0.21, spin: 100 },
  { x: 380, y: 60, width: 5, height: 5, color: "#FFFFFF", angle: 0, round: true, speed: 0.17, spin: 0 },
  { x: 150, y: 20, width: 6, height: 10, color: "#1ADB9E", angle: 30, speed: 0.2, spin: -100 },
];

/** Settings › Habits: where they show in Today, the streak's number, the rule for new habits, the milestones. */
export function HabitSettings() {
  const { model } = useWindow();
  const prefs = usePrefs();
  const flag = (key: "showsStreaks" | "neverMissTwiceDefault" | "celebratesMilestones", label: string) => (
    <label className="toggle-row">
      <span>{label}</span>
      <input type="checkbox" role="switch" checked={prefs[key]}
        onChange={(event) => { const value = event.target.checked; setPrefs((all) => ({ ...all, [key]: value })); }} />
    </label>
  );
  return (
    <section className="settings-group" aria-label={t("Habits")} data-section="habits">
      <h3>{t("Habits in Today")}</h3>
      <div className="segmented" role="radiogroup" aria-label={t("Habits in Today")}>
        {([[false, t("With the To-Dos")], [true, t("In Their Own Group")]] as const).map(([on, label]) => (
          <button key={label} role="radio" aria-checked={prefs.habitsInOwnGroup === on} className={prefs.habitsInOwnGroup === on ? "on" : ""}
            onClick={() => { setPrefs((all) => ({ ...all, habitsInOwnGroup: on })); model.setHabitsInOwnGroup(on); }}>
            {label}
          </button>
        ))}
      </div>
      <div className="toggles">
        {flag("showsStreaks", t("Show Streaks"))}
        {flag("neverMissTwiceDefault", t("Never Miss Twice for New Habits"))}
        {flag("celebratesMilestones", t("Celebrate Milestones"))}
      </div>
      <p className="settings-note">{t("Without streaks, the badge keeps the flame. Milestones: 7, 30, 100 and 365 days in a row (4, 12, 26 and 52 weeks).")}</p>
    </section>
  );
}
